/**
 * The words a Planned Outreach row and an Up Next row say, from the server's `OutreachItem`. Pure, so both
 * surfaces say the same thing and the tests read the sentences, not the markup.
 */

import type {
  OutreachItem,
  OutreachNext,
  OutreachStop,
} from "@/hooks/use-outreach";
import { businessIsoDay, formatBusinessWeekdayDay } from "@/lib/business-date";

export const KIND_LABEL: Record<OutreachItem["kind"], string> = {
  drip: "Drip",
  mission: "Mission",
  campaign: "Campaign",
};

const STOP_CHANNEL: Record<OutreachStop["channel"], string> = {
  call: "Calls",
  sms: "Texts",
  email: "Emails",
};

/** "Email “Follow-up”", "CSR Call", "Pay Link Text". */
export function touchLabel(next: OutreachNext): string {
  const title = next.title ? ` “${next.title}”` : "";
  if (next.channel === "phone")
    return next.who === "pool" ? `CSR Call${title}` : `Call${title}`;
  if (next.channel === "text")
    return next.kind === "pay_link" ? "Pay Link Text" : `Text${title}`;
  return `Email${title}`;
}

/** "Thu Nov 5", or "Waiting since Mon Nov 2" once its day has passed and it has not gone out. */
export function whenLabel(
  next: OutreachNext,
  now: Date = new Date(),
): string | null {
  if (!next.at) return null;
  const day = formatBusinessWeekdayDay(next.at);
  return businessIsoDay(next.at) < businessIsoDay(now)
    ? `Waiting since ${day}`
    : day;
}

/** Who does the next touch: "Sends automatically", "Giovanni Loaiza sends it", "Any CSR in the pool". */
export function whoLabel(next: OutreachNext): string {
  switch (next.who) {
    case "auto":
      return "Sends automatically";
    case "pool":
      return "Any CSR in the pool";
    case "sender": {
      const who = next.who_name ?? "A team member";
      if (next.channel === "phone") return `${who} calls`;
      return `${who} sends it${next.in_mailbox ? " from a draft in their mailbox" : ""}`;
    }
    case "owner":
      return next.channel === "phone"
        ? "The deal owner calls"
        : "The deal owner sends it";
  }
}

/** "Next: Email “Follow-up” · Thu Nov 5". */
export function nextLine(next: OutreachNext, now: Date = new Date()): string {
  const when = whenLabel(next, now);
  return `Next: ${touchLabel(next)}${when ? ` · ${when}` : ""}`;
}

/** "Step 2 of 3", or null for a campaign or a run with no steps yet. */
export function stepLabel(item: OutreachItem): string | null {
  return item.step && item.steps ? `Step ${item.step} of ${item.steps}` : null;
}

/** "Texts stopped: Replied STOP". */
export function stopLabel(stop: OutreachStop): string {
  return `${STOP_CHANNEL[stop.channel]} stopped: ${stop.label}`;
}

/** Whether a read has anything to show: a row or a stop. A page drops the whole section otherwise. */
export function hasOutreach(
  data: { items: unknown[]; stops: unknown[] } | undefined,
): boolean {
  return !!data && (data.items.length > 0 || data.stops.length > 0);
}

/** The rows still running or waiting, and the ones that ended inside the watch window. */
export function splitItems(items: OutreachItem[]): {
  live: OutreachItem[];
  ended: OutreachItem[];
} {
  return {
    live: items.filter((i) => i.state !== "ended"),
    ended: items.filter((i) => i.state === "ended"),
  };
}

/** The planned touches for a timeline's Up Next, soonest first (the server sorts them). */
export function upNext(
  items: OutreachItem[],
): (OutreachItem & { next: OutreachNext })[] {
  return items.filter(
    (i): i is OutreachItem & { next: OutreachNext } =>
      i.state !== "ended" && i.next !== null,
  );
}
